import React from 'react';
import { DoorOpen, MessageSquare, FileText, Shield, Radio, Coins, ArrowRight } from 'lucide-react';

const PrimitiveCard = ({ icon: Icon, title, description, badge, href }) => {
  const CardContent = () => (
    <>
      {/* Arrow indicator in top-right */}
      {href && (
        <div className="absolute top-4 right-4 md:top-6 md:right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-emerald-100 flex items-center justify-center">
            <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 text-emerald-600 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
          </div>
        </div>
      )}

      <div className="flex items-start gap-4 md:gap-6">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center group-hover:from-emerald-100 group-hover:to-teal-100 transition-all border border-emerald-100">
            <Icon className="w-5 h-5 md:w-6 md:h-6 text-emerald-600" strokeWidth={1.5} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h3 className="text-base md:text-xl font-semibold text-gray-900">{title}</h3>
            {badge && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] md:text-xs font-semibold ${
                badge === 'Beta' 
                  ? 'bg-gradient-to-r from-orange-100 to-yellow-100 text-orange-700 border border-orange-200' 
                  : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 border border-gray-300'
              }`}>
                {badge}
              </span>
            )}
          </div>
          <p className="text-sm md:text-base text-gray-600 leading-relaxed mb-3 md:mb-4">{description}</p>
          
          {/* Learn more link - only show if href is provided */}
          {href && (
            <div className="inline-flex items-center gap-1 text-xs md:text-sm font-medium text-emerald-600 group-hover:text-emerald-700 transition-colors">
              <span>Learn more</span>
              <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:translate-x-1 transition-transform" strokeWidth={2} />
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <a 
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="group relative bg-white hover:bg-gradient-to-br hover:from-emerald-50/50 hover:to-teal-50/50 rounded-xl md:rounded-2xl p-5 md:p-8 transition-all duration-300 border border-gray-200 hover:border-emerald-200 cursor-pointer hover:shadow-md block"
      >
        <CardContent />
      </a>
    );
  }

  return (
    <div className="group relative bg-white rounded-xl md:rounded-2xl p-5 md:p-8 transition-all duration-300 border border-gray-200">
      <CardContent />
    </div>
  );
};

const SocialPrimitives = () => {
  const primitives = [
    {
      icon: DoorOpen,
      title: 'Rooms',
      description: (
        <>
          Create shared spaces instantly.<br />
          Presence and session lifecycle, handled natively.
        </>
      ),
      href: 'https://docs.quicksync.me/qs-framework/session-api'
    },
    {
      icon: Shield,
      title: 'Rules',
      description: (
        <>
          Define access on your terms.<br />
          Wallet-based, temporary, or fully walletless.
        </>
      ),
      href: 'https://docs.quicksync.me/qs-framework/wallet-verification-api'
    },
    {
      icon: MessageSquare,
      title: 'Chat',
      description: (
        <>
          Real-time conversations, built for collaboration.<br />
          Threads, reactions, pins. Structured and alive.
        </>
      ),
      href: 'https://docs.quicksync.me/qs-framework/chats-api'
    },
    {
      icon: Radio,
      title: 'Real time events & Voice',
      badge: 'Beta',
      description: (
        <>
          Shared presence in real time.<br />
          Live audio and updates, always in sync.
        </>
      ),
      href: 'https://docs.quicksync.me/qs-framework/real-time-events'
    },
    {
      icon: FileText,
      title: 'Files',
      description: (
        <>
          Room-based storage, simplified.<br />
          Decentralized by default, familiar by design.
        </>
      ),
      href: 'https://docs.quicksync.me/qs-framework/upload-api'
    },
    {
      icon: Coins,
      title: 'Micropayments',
      badge: 'Coming Soon',
      description: (
        <>
          Monetize spaces natively.<br />
          Payments that feel part of the experience.
        </>
      ),
      href: null
    },
  ];

  return (
    <section className="relative bg-white py-16 md:py-32">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="mb-10 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-gray-900 mb-2">
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Build experiences users love.</span>{' '}
            <span className="text-gray-500 font-normal block sm:inline mt-1 sm:mt-0 text-xl sm:text-2xl md:text-5xl">A new standard for private workspaces.</span>
          </h2>
        </div>

        {/* Primitives Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-12 md:gap-y-8">
          {primitives.map((primitive, index) => (
            <PrimitiveCard
              key={index}
              icon={primitive.icon}
              title={primitive.title}
              description={primitive.description}
              badge={primitive.badge}
              href={primitive.href}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SocialPrimitives;
